import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, loadStoredToken, setAuthToken, formatApiError } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    loadStoredToken();
    refresh().finally(() => setReady(true));
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      if (res.data?.access_token) setAuthToken(res.data.access_token);
      setUser(res.data.user);
      return { ok: true, user: res.data.user };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAuthToken(null);
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

export function roleLabel(role) {
  return {
    platform_admin: "Platform Admin",
    company_admin: "Company Admin",
    supervisor: "Supervisor",
    employee: "Employee",
  }[role] || role;
}

export function canManageOps(user) {
  return user && ["platform_admin", "company_admin"].includes(user.role);
}
export function canConfigureTenant(user) {
  // Platform admin manages tenants via /app/tenants, not via tenant-scoped config pages.
  return user && user.role === "company_admin";
}
export function canReview(user) {
  return user && ["platform_admin", "company_admin", "supervisor"].includes(user.role);
}

export function homeFor(user) {
  if (!user) return "/login";
  if (user.role === "employee") return "/app/my-tasks";
  if (user.role === "supervisor") return "/app/reviews";
  return "/app";
}
